import React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FolderOpen, FileText, Users, User, LogOut, Building2,
  Home, FilePlus2, MessageSquare, UserCheck, Shield, CreditCard, ShieldCheck, Mail, Gavel, Sparkles, Boxes,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import NotificationBell from "./NotificationBell";

const ADMIN_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/cases", label: "Ärenden", icon: FolderOpen, testid: "nav-cases" },
  { to: "/os", label: "Bygglovs-OS", icon: Boxes, testid: "nav-os" },
  { to: "/tillsyn", label: "Tillsyn", icon: Shield, testid: "nav-tillsyn" },
  { to: "/sanction", label: "Sanktion", icon: Gavel, testid: "nav-sanction" },
  { to: "/mail-import", label: "Mailimport", icon: Mail, testid: "nav-mail-import" },
  { to: "/documents", label: "Dokument", icon: FileText, testid: "nav-documents" },
  { to: "/contacts", label: "Kontakter", icon: Users, testid: "nav-contacts" },
  { to: "/audit-log", label: "AI-utlåtanden", icon: ShieldCheck, testid: "nav-audit-log" },
  { to: "/billing/manage", label: "Prenumeration", icon: CreditCard, testid: "nav-billing" },
  { to: "/profile", label: "Profil", icon: User, testid: "nav-profile" },
];

const CITIZEN_NAV = [
  { to: "/projects", label: "Projekt", icon: FolderOpen, testid: "nav-projects" },
  { to: "/ansok", label: "Ansök", icon: FilePlus2, testid: "nav-ansok" },
  { to: "/chat", label: "AI-råd", icon: MessageSquare, testid: "nav-chat" },
  { to: "/messages", label: "Inkorg", icon: MessageSquare, testid: "nav-messages" },
  { to: "/profile", label: "Profil", icon: User, testid: "nav-profile" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const NAV = isAdmin ? ADMIN_NAV : CITIZEN_NAV;

  // Hide bottom nav on detail screens & wizard
  const isDetail = /^\/cases\/[^/]+/.test(location.pathname) || location.pathname.startsWith("/ansok");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-zinc-200 fixed inset-y-0 left-0">
        <div className="px-6 py-6 border-b border-zinc-200 flex items-center gap-3">
          <img src="/logo-icon.png" alt="Bygglovskompassen" className="w-9 h-9 object-contain" />
          <div>
            <div className="text-base font-semibold text-zinc-900 leading-tight" style={{ fontFamily: "Outfit" }}>
              Bygglovskompassen
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              {isAdmin ? "Handläggarvy" : "Min sida"}
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`sidebar-${n.testid}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition ${
                  isActive ? "bg-blue-700 text-white" : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
            >
              <n.icon className="w-4 h-4 stroke-[1.5]" />
              {n.label}
            </NavLink>
          ))}
          {!isAdmin && (
            <NavLink
              to="/min-handlaggare"
              data-testid="sidebar-nav-handlaggare"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition ${
                  isActive ? "bg-blue-700 text-white" : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
            >
              <UserCheck className="w-4 h-4 stroke-[1.5]" />
              Min handläggare
            </NavLink>
          )}
          {isAdmin && (
            <NavLink
              to="/admin"
              data-testid="sidebar-nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition ${
                  isActive ? "bg-blue-700 text-white" : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
            >
              <Building2 className="w-4 h-4 stroke-[1.5]" />
              Adminpanel
            </NavLink>
          )}
          {isAdmin && (
            <NavLink
              to="/admin/ai-stats"
              data-testid="sidebar-nav-ai-stats"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition ${
                  isActive ? "bg-purple-700 text-white" : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
            >
              <Sparkles className="w-4 h-4 stroke-[1.5]" />
              AI-statistik
            </NavLink>
          )}
        </nav>
        <div className="px-3 py-4 border-t border-zinc-200">
          <div className="px-3 py-2 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-zinc-100 text-zinc-700 flex items-center justify-center text-xs font-semibold">
              {user?.name?.[0] || "?"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-zinc-900 truncate">{user?.name}</div>
              <div className="text-xs text-zinc-500 truncate">
                {isAdmin ? "Handläggare" : "Privatperson"}
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="sidebar-logout"
              className="p-2 rounded-sm hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900"
              title="Logga ut"
            >
              <LogOut className="w-4 h-4 stroke-[1.5]" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="bg-white/90 backdrop-blur-xl border-b border-zinc-200 sticky top-0 z-30">
          <div className="px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
            <div className="lg:hidden flex items-center gap-2">
              <img src="/logo-icon.png" alt="Bygglovskompassen" className="w-8 h-8 object-contain" />
              <span className="font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>Bygglovskompassen</span>
            </div>
            <div className="hidden lg:block text-xs uppercase tracking-[0.2em] text-zinc-500">
              {location.pathname.replace("/", "") || (isAdmin ? "Dashboard" : "Hem")}
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
            </div>
          </div>
        </header>

        <main className="flex-1 pb-24 lg:pb-10">
          <Outlet />
        </main>

        {/* Bottom nav (mobile) */}
        {!isDetail && (
          <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-zinc-200 z-40">
            <div className={NAV.length === 6 ? "grid grid-cols-6" : "grid grid-cols-5"}>
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`bottom-${n.testid}`}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center py-2.5 gap-0.5 transition ${
                      isActive ? "text-blue-700" : "text-zinc-500"
                    }`
                  }
                >
                  <n.icon className="w-5 h-5 stroke-[1.5]" />
                  <span className="text-[10px] font-medium">{n.label}</span>
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </div>
    </div>
  );
}
